import {computed, defineComponent} from 'vue';
import {useField} from "vee-validate";
import {AthumiService, type PaymentSession} from "@/api/enterprise-gateway-api.proxy.ts";
import {useFormatting} from "@/composables/currency-format.ts";
import {useRouter} from "vue-router";

import mastercard from '@/assets/mastercard.svg';
import visa from '@/assets/visa.svg';
import bancontact from '@/assets/bancontact.svg';

export default defineComponent({
    name: 'AegPaymentMethod',
    emits: ['add-prepaid'],
    props: {
        paymentMethod: {
            type: String,
            required: true,
        },
        paymentSession: {
            type: Object as () => PaymentSession,
            required: true,
        },
        allowPrepaid: {
            type: Boolean,
            required: true,
            default: true,
        },
    },
    setup(props, { emit }) {
        const router = useRouter();
        const {value: inputValue} = useField('paymentMethod');
        const {formatCurrency} = useFormatting();

        const prepaidDisabled = computed(() => {
            return !userHadPrepaidBefore.value || !hasSufficientPrepaidBalance.value;
        });
        
        const allowPrepaid = computed(() => { return props.allowPrepaid })

        const hasSufficientPrepaidBalance = computed(() => {
            const balance = props.paymentSession.currentServiceSaldo;
            const totalAmount = props.paymentSession.totalAmountInclVat;

            if (!balance || totalAmount === null || totalAmount <= 0) {
                return false;
            }

            const isValidAmount = totalAmount > 0;
            const hasEnoughBalance = balance >= totalAmount;
            inputValue.value = isValidAmount && hasEnoughBalance ? 'prepaid' : 'card';
            return isValidAmount && hasEnoughBalance;
        });

        const prepaidBalanceDeficiency = computed<number | undefined>(() => {
            const balance = props.paymentSession.currentServiceSaldo;
            const totalAmount = props.paymentSession.totalAmountInclVat;

            // use == null, this checks if variable = null or = undefined, !var also return true if var = 0
            if (balance == null || totalAmount == null) {
                return undefined;
            }

            if (hasSufficientPrepaidBalance.value) {
                return undefined;
            }

            return Number((totalAmount - balance).toFixed(2));
        });

        const prepaidBalance = computed(() => {
            return props.paymentSession.currentServiceSaldo;
        });

        const prepaidTitle = computed(() => {
            if (props.paymentSession.service === AthumiService.ELys) {
                return 'Met eLys-saldo';
            }
            if (props.paymentSession.service === AthumiService.Klip) {
                return 'Met KLIP-saldo';
            }
            if (props.paymentSession.service === AthumiService.Vip) {
                return 'Met VIP-saldo';
            }
            return 'Met prepaid saldo'
        })

        const userHadPrepaidBefore = computed(() => {
            if (!props.paymentSession) {
                return false;
            }
            return props.paymentSession.currentServiceSaldo !== null;
        });

        const addPrepaid = async () => {
           emit('add-prepaid');
        }

        return {
            mastercard,
            bancontact,
            visa,
            inputValue,
            userHadPrepaidBefore,
            hasSufficientPrepaidBalance,
            prepaidBalanceDeficiency,
            prepaidBalance,
            prepaidDisabled,
            prepaidTitle,
            allowPrepaid,
            formatCurrency,
            addPrepaid
        }
    }
})
